import { useTranslation } from "react-i18next";
import { Select, Tooltip } from "antd";
import { FileExclamationOutlined, PictureOutlined, RedoOutlined } from "@ant-design/icons";
import { BsFillMicFill } from "react-icons/bs";
import { useUser } from "../../../core/context/UserContext";
import { DATE_OPTIONS } from "../../../core/utils/date-utils";
import StudyTaskTypeEnum from "../../../enums/StudyTaskTypeEnum";
import {
    fieldMatches,
    isEmpty,
    QuickDateRange,
    QuickModalityFilter,
    QuickResetLink,
    QuickSearch,
    useAppliedFilter,
    useQuickSearch,
} from "../../quick-filter/QuickFilterParts";
import { ExtraFiltersButton, ExtraFilterTags, isFieldActive } from "../../quick-filter/ExtraFilters";
import { buildDefaultStudyFilter, QUICK_FILTER_RESET, quickFilters } from "./study-quick-filters";
import { STUDY_EXTRA_FIELDS } from "./study-extra-filters";

// Etiketler kısa ("Son 5G"): çubuk tek satıra sığsın diye. Son 5 gün sayfanın varsayılanı.
const DATE_PRESETS = [
    { key: 'today', label: 'Today', option: DATE_OPTIONS.TODAY },
    { key: 'yesterday', label: 'Yesterday', option: DATE_OPTIONS.YESTERDAY },
    { key: 'last5', label: 'Last 5D', option: DATE_OPTIONS.LAST_5_DAYS },
    { key: 'last30', label: 'Last 30D', option: DATE_OPTIONS.LAST_30_DAYS },
];

// Çubukta görünen iş listeleri: GetDataGrid loglarında (Eylül 2026, 7 gün) en çok seçilen dört hazır filtre.
// Kalanlar ("Benim okuyacaklarım" vb.) sol panelde durur.
// Tipe göre gruplanır: tipi grubun rengi ve etiketi söyler, düğmede yalnız aşama (okunacak/yazılacak) yazar —
// kullanıcı yazıyı okumadan grubu renginden tanır. Her düğme yine TEK tıkla tek hazır filtreyi uygular.
const PRESET_GROUPS = [
    {
        key: 'emergency',
        label: 'Emergency',
        presets: [
            { label: 'Emergency, Readings', short: 'To be read' },
            { label: 'Emergency, to Write', short: 'To be write' },
        ],
    },
    {
        key: 'routine',
        label: 'Routine',
        presets: [
            { label: 'Routine readings', short: 'To be read' },
            { label: 'Routine writings', short: 'To be write' },
        ],
    },
];

const BAR_PRESETS = PRESET_GROUPS.flatMap(group => group.presets.map(p => p.label));

// Atama kartı: serbest = hiç kimseye atanmamış, atanmış = yalnız bana atanmış; ikisi birlikte seçilebilir.
const ASSIGN_OPTIONS = [
    { mode: 'free', label: 'QF_ASSIGN_FREE', hint: 'QF_ASSIGN_FREE_HINT' },
    { mode: 'mine', label: 'QF_ASSIGN_MINE', hint: 'QF_ASSIGN_MINE_HINT' },
];

// Atama değeri: serbest '-1', atanmış kullanıcının id'si; ikisi birlikte seçiliyse VEYA olarak virgülle
// birleşir ("-1,<id>", bkz. sunucuda StudyFilter). Hiçbiri seçili değilse filtre yok ("Tüm").
const assignValueOf = ({ free, mine }, userId) => [free && '-1', mine && userId].filter(Boolean).join(',') || null;

// Ses kaydı: var / yok; basılıya tekrar tıklamak filtreyi kaldırır.
const VOICE_OPTIONS = [
    { value: true, label: 'QF_VOICE_YES', hint: 'Has Voice Record' },
    { value: false, label: 'QF_VOICE_NO', hint: 'No Voice Record' },
];

// Rapor/görev durumu açılır listesi: seçenekler birbirini dışlar, aynı anda yalnız biri seçilir.
// Görev seçenekleri o tipte AÇIK görevi olan çekimleri süzer (bkz. StudyFilter.OpenTaskType); diğer filtrelere
// eklenen bir koşuldur, kullanıcının görev görme izninden bağımsızdır.
const WORK_STATE_OPTIONS = [
    {
        key: 'not-written',
        label: 'Report not written',
        icon: <FileExclamationOutlined aria-hidden="true" />,
        changes: { isReportWritten: false },
    },
    {
        key: 'missing-image',
        label: 'QF_TASK_MISSING_IMAGE',
        icon: <PictureOutlined aria-hidden="true" />,
        changes: { openTaskType: StudyTaskTypeEnum.MISSING_IMAGE },
    },
    {
        key: 're-assessment',
        label: 'QF_TASK_RE_ASSESSMENT',
        icon: <RedoOutlined aria-hidden="true" />,
        changes: { openTaskType: StudyTaskTypeEnum.RE_ASSESSMENT },
    },
];

// Atamanın bakıldığı alan aşamadan gelir: okunacakta doktor, yazılacakta raportör. Aşama yoksa kart pasif.
const assignKeyFor = (isRead) => {
    if (isRead === false)
        return 'assignDoctor';
    if (isRead === true)
        return 'assignReporter';
    return null;
};

/**
 * Çekim grid'inin üstündeki hızlı filtre çubuğu: grid kartından ayrı, her blok (tarih, iş listesi, atama, durum,
 * ses, modalite, diğer filtreler) yan yana kendi küçük kartında; tarih ile serbest arama üst satırda. Ortak parçalar
 * quick-filter/ altında (rapor grid'i de kullanır); burada yalnız çekime özgü bloklar var.
 * Amaç: filtrelemeyi sol paneli açmadan yaptırmak — uzun vadede sol panelin yerini alır.
 */
export default function StudyQuickFilterBar() {
    const { t } = useTranslation();
    const { user } = useUser();
    const { filter, apply, replace, ready } = useAppliedFilter('studyFilter');
    const search = useQuickSearch(filter, apply);

    if (!ready)
        return null;

    const userId = user?.data?.id;

    // Panelde hangi tarih türü seçiliyse çubuk onu yönetir: çekim tarihi doluysa çekim, değilse geliş.
    const useStudyDate = !!(filter.studyDateTimeBegin || filter.studyDateTimeEnd);

    // ---- atama ----
    const assignKey = assignKeyFor(filter.isRead);
    const assignParts = assignKey && !isEmpty(filter[assignKey]) ? `${filter[assignKey]}`.split(',') : [];
    // Kart yalnız kendi üretebileceği değeri tanır; panelden seçilmiş başka bir kullanıcı etiket olarak kalır.
    const assignManaged = assignParts.length > 0 && assignParts.every(p => p === '-1' || (!!userId && p === userId));
    const assign = {
        free: assignManaged && assignParts.includes('-1'),
        mine: assignManaged && assignParts.includes(userId),
    };
    // Kartın yönettiği alan hazır filtre eşleşmesinde ve "Diğer filtreler" etiketlerinde sayılmaz.
    const managedAssignKey = assignManaged ? assignKey : null;

    const toggleAssign = (mode) => apply({ [assignKey]: assignValueOf({ ...assign, [mode]: !assign[mode] }, userId) });

    // ---- iş listesi ----
    const presets = quickFilters(userId);
    const barPresets = BAR_PRESETS.map(label => presets.find(p => p.label === label)).filter(Boolean);
    const workMatches = (fields) => Object.entries(fields)
        .every(([key, value]) => key === managedAssignKey || fieldMatches(value, filter[key]));
    const matchesPreset = (p) => workMatches({ ...QUICK_FILTER_RESET, ...p.filters });
    const noPreset = workMatches(QUICK_FILTER_RESET);
    // İş listesi alanları çubuktaki hiçbir seçimle eşleşmiyor → "Diğer filtreler"de etiket olarak gösterilir.
    const customWork = !noPreset && !barPresets.some(matchesPreset);
    // Basılı hazır filtrenin tip grubu: atama kartı onun rengini alır (acil kırmızı, rutin mavi).
    const activeGroup = PRESET_GROUPS.find(group => group.presets.some(({ label }) => {
        const preset = barPresets.find(p => p.label === label);
        return preset && matchesPreset(preset);
    }));

    // Serbest/atanmış seçimi hazır filtre değişince korunur; aşama değişirse doktor ↔ raportör alanına taşınır.
    const applyPreset = (preset) => {
        const next = {
            ...QUICK_FILTER_RESET,
            ...(preset?.filters || {}),
            quickFilters: preset?.label,
        };
        const nextAssignKey = assignKeyFor(next.isRead);
        if (assignManaged && nextAssignKey)
            next[nextAssignKey] = assignValueOf(assign, userId);
        apply(next);
    };

    // ---- durum ----
    const workState = WORK_STATE_OPTIONS
        .find(option => Object.entries(option.changes).every(([key, value]) => filter[key] === value))?.key;
    // Yalnız bu listenin kendi değerleri temizlenir: paneldeki "Rapor yazılmış" gibi başka bir seçim korunur.
    const applyWorkState = (key) => apply({
        isReportWritten: filter.isReportWritten === false ? null : filter.isReportWritten,
        openTaskType: null,
        ...(WORK_STATE_OPTIONS.find(option => option.key === key)?.changes || {}),
    });
    const voiceRecord = filter.hasVoiceRecord ?? null;

    // ---- diğer filtreler: iş listesi alanları yalnız çubukta karşılığı yoksa etiket olur ----
    const tagFields = STUDY_EXTRA_FIELDS.filter(field => (!field.work || customWork) && field.key !== managedAssignKey);
    const extraCount = tagFields.filter(field => isFieldActive(field, filter)).length;

    return (
        <div className="qf">
            <div className="qf-row qf-row-top">
                <QuickDateRange
                    filter={filter}
                    apply={apply}
                    beginKey={useStudyDate ? 'studyDateTimeBegin' : 'studyReceivedDateTimeBegin'}
                    endKey={useStudyDate ? 'studyDateTimeEnd' : 'studyReceivedDateTimeEnd'}
                    presets={DATE_PRESETS}
                    disabled={search.active}
                    allowClear
                    label={t(useStudyDate ? 'Study Date' : 'Received Date')}
                />

                <QuickSearch search={search} hint={t('QF_SEARCH_HINT')} />
            </div>

            <div className="qf-row">
                <div className="qf-group" role="group" aria-label={t('Quick Filters')}>
                    <div className="qf-seg">
                        <button type="button" aria-pressed={noPreset} onClick={() => applyPreset(null)}>
                            {t('All')}
                        </button>
                    </div>
                    {PRESET_GROUPS.map(group => (
                        <div key={group.key} className={`qf-seg qf-seg-${group.key}`}>
                            <span className="qf-seg-tag">{t(group.label)}</span>
                            {group.presets.map(({ label, short }) => {
                                const preset = barPresets.find(p => p.label === label);
                                if (!preset)
                                    return null;

                                const active = matchesPreset(preset);
                                return (
                                    <Tooltip key={label} title={t(label)} mouseEnterDelay={0.6}>
                                        <button
                                            type="button"
                                            aria-label={t(label)}
                                            aria-pressed={active}
                                            onClick={() => applyPreset(active ? null : preset)}
                                        >
                                            {t(short)}
                                        </button>
                                    </Tooltip>
                                );
                            })}
                        </div>
                    ))}
                </div>

                {/* Pasif düğmeler fare olayı üretmez → açıklama kartın kendisinde. */}
                <Tooltip title={assignKey ? null : t('QF_ASSIGN_NEEDS_STAGE')} mouseEnterDelay={0.3}>
                    <div
                        className={`qf-group${assignKey ? '' : ' is-disabled'}`}
                        role="group"
                        aria-label={t('QF_ASSIGN_GROUP')}
                    >
                        <div className={`qf-seg${activeGroup ? ` qf-seg-${activeGroup.key}` : ''}`}>
                            <button
                                type="button"
                                disabled={!assignKey}
                                aria-pressed={assignParts.length === 0}
                                onClick={() => apply({ [assignKey]: null })}
                            >
                                {t('All')}
                            </button>
                            {ASSIGN_OPTIONS.map(({ mode, label, hint }) => (
                                <Tooltip key={mode} title={t(hint)} mouseEnterDelay={0.6}>
                                    <button
                                        type="button"
                                        disabled={!assignKey || (mode === 'mine' && !userId)}
                                        aria-pressed={assign[mode]}
                                        onClick={() => toggleAssign(mode)}
                                    >
                                        {t(label)}
                                    </button>
                                </Tooltip>
                            ))}
                        </div>
                    </div>
                </Tooltip>

                <div className="qf-group">
                    <Select
                        size="small"
                        allowClear
                        className={`qf-select${workState ? ' is-active' : ''}`}
                        popupMatchSelectWidth={false}
                        placeholder={t('QF_WORK_STATE')}
                        value={workState}
                        onChange={applyWorkState}
                        options={WORK_STATE_OPTIONS.map(({ key, label, icon }) => ({
                            value: key,
                            label: <span className="qf-option">{icon}{t(label)}</span>,
                        }))}
                    />
                </div>

                <div className="qf-group">
                    <div className="qf-seg" role="group" aria-label={t('QF_VOICE')}>
                        <span className="qf-seg-label">
                            <BsFillMicFill aria-hidden="true" />
                            {t('QF_VOICE')}
                        </span>
                        {VOICE_OPTIONS.map(({ value, label, hint }) => (
                            <Tooltip key={label} title={t(hint)} mouseEnterDelay={0.6}>
                                <button
                                    type="button"
                                    aria-pressed={voiceRecord === value}
                                    onClick={() => apply({ hasVoiceRecord: voiceRecord === value ? null : value })}
                                >
                                    {t(label)}
                                </button>
                            </Tooltip>
                        ))}
                    </div>
                </div>

                <QuickModalityFilter filter={filter} apply={apply} />

                <div className="qf-group">
                    <ExtraFiltersButton
                        fields={STUDY_EXTRA_FIELDS}
                        filter={filter}
                        apply={apply}
                        activeCount={extraCount}
                    />
                </div>

                <QuickResetLink onClick={() => replace(buildDefaultStudyFilter())} />
            </div>

            <ExtraFilterTags fields={tagFields} filter={filter} apply={apply} />
        </div>
    );
}
